import React from 'react';
import PrivacyPageHeader from './components/PrivacyPageHeader';
import PrivacyPageFooter from './components/PrivacyPageFooter';
import PolicySection from './components/PolicySection';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      <PrivacyPageHeader
        title="SiteProof Privacy Policy"
        lastUpdated="28 March 2026"
      />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Data controller identification */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-8">
          <p className="text-sm text-slate-400">
            This Privacy Policy explains how <strong className="text-slate-200">SiteProof Ltd</strong> (Company
            number: <strong className="text-slate-200">17085500</strong>, registered in England and Wales) collects,
            uses, and protects your personal data when you use the SiteProof platform. SiteProof Ltd is the data
            controller for personal data processed in connection with your account.
          </p>
        </div>

        <PolicySection number="1" title="Data We Collect">
          <p>When you use SiteProof, we may collect the following categories of personal data:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><strong className="text-slate-300">Account data:</strong> name, email address, job title, company name, phone number</li>
            <li><strong className="text-slate-300">Site data:</strong> construction site details, addresses, task records, snag lists, inspection reports, site diary entries, and compliance documents</li>
            <li><strong className="text-slate-300">Worker data:</strong> worker profiles, certifications, qualifications, assignments, task completions, and induction records</li>
            <li><strong className="text-slate-300">Photos and files:</strong> images, drawings, documents, certificates, and other files uploaded to the platform</li>
            <li><strong className="text-slate-300">Usage data:</strong> pages visited, features used, login timestamps, and activity logs</li>
            <li><strong className="text-slate-300">Payment data:</strong> billing information processed securely by Stripe (we do not store card details directly)</li>
            <li><strong className="text-slate-300">Technical data:</strong> IP address, browser type, device information, and session data</li>
          </ul>
        </PolicySection>

        <PolicySection number="2" title="How We Use Your Data">
          <p>SiteProof Ltd processes your personal data for the following purposes:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Providing and operating the SiteProof platform</li>
            <li>Managing your account and subscription</li>
            <li>Sending service notifications, alerts, and updates</li>
            <li>Processing payments and managing billing</li>
            <li>Improving platform features and user experience</li>
            <li>Complying with legal obligations</li>
            <li>Responding to support requests</li>
            <li>Detecting and preventing fraud or misuse of the platform</li>
          </ul>
          <p>
            Our lawful bases for processing under UK GDPR are: contract performance (to provide the service you
            have subscribed to), legitimate interests (to improve the platform and prevent fraud), and legal
            obligation (to comply with applicable law).
          </p>
        </PolicySection>

        <PolicySection number="3" title="Where Your Data Is Stored">
          <p>
            Your data is stored on secure cloud infrastructure provided by Supabase and Amazon Web Services
            (AWS). Data is primarily stored within the United Kingdom and the European Economic Area (EEA).
          </p>
          <p>
            Files, photos, drawings, and documents uploaded to SiteProof are stored in secure cloud storage
            buckets. Access to stored data is controlled by role-based permissions and encrypted in transit
            and at rest.
          </p>
          <p>
            Where data is processed by third-party sub-processors outside the UK or EEA (for example, Stripe
            or Resend in the USA), appropriate safeguards are in place including Standard Contractual Clauses
            (SCCs) in accordance with UK GDPR requirements.
          </p>
        </PolicySection>

        <PolicySection number="4" title="Data Sharing">
          <p>
            SiteProof Ltd does not sell your personal data. We may share data with trusted third-party service
            providers who assist in operating the platform, including:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><strong className="text-slate-300">Supabase</strong> — database and authentication infrastructure (UK/EU)</li>
            <li><strong className="text-slate-300">Stripe</strong> — payment processing (USA, SCCs in place)</li>
            <li><strong className="text-slate-300">Amazon Web Services (AWS)</strong> — cloud infrastructure and AI features (UK/EU)</li>
            <li><strong className="text-slate-300">Resend</strong> — transactional email delivery (USA, SCCs in place)</li>
          </ul>
          <p>
            All third-party processors are contractually bound to process data only on our instructions and in
            accordance with applicable data protection law.
          </p>
          <p>
            We may disclose personal data where required by law, court order, or regulatory authority.
          </p>
        </PolicySection>

        <PolicySection number="5" title="Data Retention">
          <p>
            We retain your personal data for as long as your account is active or as needed to provide services.
            Upon account termination:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Account and site data is retained for 30 days to allow data export</li>
            <li>After 30 days, data is permanently deleted from our live systems</li>
            <li>Backup copies may persist for up to 90 days before being overwritten</li>
            <li>Financial records are retained for 7 years as required by UK law</li>
          </ul>
          <p>
            You may request deletion of your data at any time by contacting us at{' '}
            <a href="mailto:support@site-proof.app" className="text-emerald-400 hover:text-emerald-300">
              support@site-proof.app
            </a>.
          </p>
        </PolicySection>

        <PolicySection number="6" title="Your Rights Under UK GDPR">
          <p>As a data subject, you have the following rights:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><strong className="text-slate-300">Right of access:</strong> request a copy of the personal data we hold about you</li>
            <li><strong className="text-slate-300">Right to rectification:</strong> request correction of inaccurate or incomplete data</li>
            <li><strong className="text-slate-300">Right to erasure:</strong> request deletion of your personal data in certain circumstances</li>
            <li><strong className="text-slate-300">Right to restriction:</strong> request that we limit processing of your data</li>
            <li><strong className="text-slate-300">Right to portability:</strong> receive your data in a structured, machine-readable format</li>
            <li><strong className="text-slate-300">Right to object:</strong> object to processing based on legitimate interests</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:support@site-proof.app" className="text-emerald-400 hover:text-emerald-300">
              support@site-proof.app
            </a>. We will respond within 30 days.
          </p>
          <p>
            You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at{' '}
            <a
              href="https://ico.org.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:text-emerald-300"
            >
              ico.org.uk
            </a>.
          </p>
        </PolicySection>

        <PolicySection number="7" title="Cookies">
          <p>
            SiteProof uses cookies and similar technologies to operate the platform and maintain your login
            session. For full details of the cookies we use, please see our{' '}
            <a href="/cookie-policy" className="text-emerald-400 hover:text-emerald-300 underline">
              Cookie Policy
            </a>.
          </p>
        </PolicySection>

        <PolicySection number="8" title="Security">
          <p>
            SiteProof Ltd implements appropriate technical and organisational measures to protect your personal
            data against unauthorised access, loss, or disclosure. These measures include:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Encryption of data in transit (TLS) and at rest</li>
            <li>Role-based access controls limiting data access to authorised users only</li>
            <li>Regular security reviews and infrastructure monitoring</li>
            <li>Secure authentication with email verification</li>
            <li>Logical data isolation between customer accounts</li>
          </ul>
          <p>
            While we take all reasonable steps to protect your data, no system is completely secure. Please
            notify us immediately at{' '}
            <a href="mailto:support@site-proof.app" className="text-emerald-400 hover:text-emerald-300">
              support@site-proof.app
            </a>{' '}
            if you suspect any security breach.
          </p>
        </PolicySection>

        <PolicySection number="9" title="Changes to This Policy">
          <p>
            SiteProof Ltd may update this Privacy Policy from time to time. We will notify you of significant
            changes by email or via an in-platform notification. The effective date at the top of this page
            indicates when the policy was last revised.
          </p>
          <p>
            Continued use of SiteProof after a policy update constitutes acceptance of the revised policy.
          </p>
        </PolicySection>

        <PolicySection number="10" title="Contact Us">
          <p>
            For any privacy-related questions, data subject requests, or concerns, please contact SiteProof Ltd:
          </p>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 mt-3">
            <p className="font-medium text-slate-200">SiteProof Ltd — Data Controller</p>
            <p>Registered in England and Wales</p>
            <p>Company number: 17085500</p>
            <p>
              Email:{' '}
              <a href="mailto:support@site-proof.app" className="text-emerald-400 hover:text-emerald-300">
                support@site-proof.app
              </a>
            </p>
          </div>
        </PolicySection>
      </main>

      <PrivacyPageFooter />
    </div>
  );
};

export default PrivacyPolicy;
